import React, { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from "lucide-react";
import { PageLightbox } from "./PageLightbox";
import { Accent, Container, SectionHeader } from "./ui";
import { samplePages } from "../data/content";
export function SamplesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    function onScroll() {
      if (!track) return;
      const slides = Array.from(track.children) as HTMLElement[];
      const base = slides[0]?.offsetLeft ?? 0;
      const left = track.scrollLeft;
      let closest = 0;
      slides.forEach((s, i) => {
        if (Math.abs(s.offsetLeft - base - left) < Math.abs(slides[closest].offsetLeft - base - left)) {
          closest = i;
        }
      });
      setActive(closest);
    }
    track.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);
  function goTo(index: number) {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slides = Array.from(track.children) as HTMLElement[];
    track.scrollTo({
      left: slides[clamped].offsetLeft - slides[0].offsetLeft,
      behavior: "smooth"
    });
  }
  const arrow = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-petrol/40 bg-white text-petrol transition-colors duration-150 ease-out hover:bg-petrol hover:text-white disabled:pointer-events-none disabled:opacity-30";
  return <section className="bg-white py-12 md:py-24" aria-label="Sample pages" data-id="element-170" data-mp="2b6s0vfoxhium">
      <Container data-id="element-171" data-mp="edzy4vlrw4pn">
        <SectionHeader eyebrow="Look inside" title={<>
              See the pages <Accent data-id="element-173" data-mp="9foqmriya6al">before you print</Accent>
            </>} lead="A selection of real pages from the kit. Tap any page to read it larger." data-id="element-172" data-mp="ecf6bdquezvh" />

        <div data-id="element-174" data-mp="xa8kxpp2icta">
          <div className="min-w-0" aria-roledescription="carousel" data-id="element-175" data-mp="x9ywi6jo8dpp">
            <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-6 pt-1" data-id="element-176" data-mp="jiafboukez1v">
              {samplePages.map((p, i) => <figure key={p.src} aria-label={`${i + 1} of ${samplePages.length}`} className="w-[78%] shrink-0 snap-start sm:w-[calc((100%-16px)/2)] lg:w-[calc((100%-48px)/4)]" data-id="element-177" data-mp="1c6g4qowkdc81" data-mpi={i}>
                  <button type="button" onClick={() => setZoomed(i)} className="group relative block w-full overflow-hidden rounded-xl border border-line bg-cream shadow-[0_12px_30px_rgba(22,78,85,0.08)]" aria-label={`View larger: ${p.label}`} data-id="element-178" data-mp="128jwq5yxaxe5">
                    <img src={p.src} alt={p.label} loading="lazy" className="aspect-[9/16] w-full object-cover object-top" data-id="element-179" data-mp="3ke3441bt0alj" />
                    <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-petrol shadow transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-180" data-mp="3fegib3hwtm14">
                      <ZoomInIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="3ogzfp82xf6fz" />
                    </span>
                  </button>
                  <figcaption className="px-1 pt-3 text-sm font-semibold text-ink" data-id="element-182" data-mp="2wp0di3z6z7ew">{p.label}</figcaption>
                </figure>)}
            </div>

            <div className="mt-2 flex items-center justify-center gap-5" data-id="element-183" data-mp="3ahzq9y7lanir">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous sample" className={arrow} data-id="element-184" data-mp="35onggu7kkla2">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-185" data-mp="2hy18f2xkf102" />
              </button>
              <div className="flex gap-2" data-id="element-186" data-mp="2cyydwcf7od6c">
                {samplePages.map((p, i) => <button key={p.src} type="button" onClick={() => goTo(i)} aria-label={`Go to sample ${i + 1}`} aria-current={i === active} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? "w-7 bg-petrol" : "w-2.5 bg-petrol/25 hover:bg-petrol/45"}`} data-id="element-187" data-mp="2qpkyvxaiy65p" data-mpi={i} />)}
              </div>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === samplePages.length - 1} aria-label="Next sample" className={arrow} data-id="element-188" data-mp="3jcffgj480ldw">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-189" data-mp="1ozpj7uqdr03c" />
              </button>
            </div>
          </div>
        </div>
      </Container>

      <PageLightbox pages={samplePages} index={zoomed} onClose={() => setZoomed(null)} onChange={setZoomed} data-id="element-190" data-mp="b24jnbvftz8o" />
    </section>;
}